import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { useCallback } from 'react';

import { type GetBoardStatsResponse } from '@air/api';
import { type Board } from '@air/api/types';

import { GalleryItemType } from '~/components/Gallery/types';
import { useInvalidatePrivateBoard } from '~/components/v2/hooks/queries/useInvalidatePrivateBoard';
import { isPrivateBoardStatsKey } from '~/components/v2/hooks/queries/usePrivateBoardsStats';

export const useUpdateBoardStats = () => {
  const queryClient = useQueryClient();
  const { invalidatePrivateBoard } = useInvalidatePrivateBoard();
  const updateBoardStats = useCallback(
    async (
      boardId: Board['id'],
      updateFn?: (data?: GetBoardStatsResponse) => GetBoardStatsResponse | undefined,
      revalidate = true,
    ) => {
      const boardStatsPredicate = ({ queryKey }: { queryKey: QueryKey }) =>
        isPrivateBoardStatsKey(queryKey) && queryKey[1].boardId === boardId;

      await queryClient.cancelQueries({ predicate: boardStatsPredicate });

      if (updateFn) {
        const boardStatsQueries = queryClient.getQueriesData({
          predicate: boardStatsPredicate,
        });

        boardStatsQueries.forEach(([queryKey]) => {
          queryClient.setQueryData<GetBoardStatsResponse | undefined>(queryKey, updateFn);
        });
      }

      if (revalidate) {
        void queryClient.invalidateQueries({ predicate: boardStatsPredicate });
        // The board carries the same counts as `directDescendantCount` and
        // `directDescendantBoardCount`, which are replacing this deprecated endpoint.
        invalidatePrivateBoard(boardId);
      }
    },
    [invalidatePrivateBoard, queryClient],
  );

  return {
    updateBoardStats,
  };
};

export const useUpdateItemsCount = () => {
  const { updateBoardStats } = useUpdateBoardStats();

  const removeItems = useCallback(
    (boardId: Board['id'], count: number, type: GalleryItemType.asset | GalleryItemType.file) => {
      return updateBoardStats(
        boardId,
        (data) =>
          data &&
          produce(data, (draft) => {
            if (type === GalleryItemType.file) {
              draft.fileCount = Math.max(0, draft.fileCount - count);
            } else {
              draft.assetCount = Math.max(0, draft.assetCount - count);
            }
          }),
      );
    },
    [updateBoardStats],
  );

  return {
    removeItems,
  };
};
